import { useAuth } from '../context/AuthContext';
import { useState, useEffect, useCallback } from 'react';

// Helper functions defined outside the hook to avoid dependency issues
const normalizePermissions = (perms) => {
  if (!perms) return getDefaultPermissions();
  
  // If it's in the old format (canViewBookings, etc.), convert it
  if ('canViewBookings' in perms) {
    return {
      bookings: [
        ...(perms.canViewBookings ? ['view'] : []),
        ...(perms.canEditBookings ? ['edit', 'create'] : []),
        ...(perms.canDeleteBookings ? ['delete'] : []),
        ...(perms.canExportBookings ? ['export'] : [])
      ],
      leads: [
        ...(perms.canViewLeads ? ['view'] : []),
        ...(perms.canEditLeads ? ['edit', 'create'] : []),
        ...(perms.canDeleteLeads ? ['delete'] : []),
        ...(perms.canExportLeads ? ['export'] : [])
      ],
      venues: [
        ...(perms.canViewVenues ? ['view'] : []),
        ...(perms.canEditVenues ? ['edit'] : [])
      ],
      inventory: [
        ...(perms.canViewInventory ? ['view'] : []),
        ...(perms.canEditInventory ? ['edit'] : [])
      ],
      expenses: [
        ...(perms.canViewExpenses !== false ? ['view'] : []),
        ...(perms.canEditExpenses ? ['edit', 'create'] : []),
        ...(perms.canDeleteExpenses ? ['delete'] : []),
        ...(perms.canExportExpenses ? ['export'] : [])
      ],
      accounts: [
        ...(perms.canViewAccounts ? ['view'] : []),
        ...(perms.canEditAccounts ? ['edit', 'create', 'delete', 'export'] : [])
      ],
      financials: perms.canViewReports ? ['view', 'export'] : [],
      reports: perms.canViewReports ? ['view', 'export'] : [],
      bookingAnalytics: perms.canExportBookingAnalytics ? ['export'] : [],
      financialAnalytics: perms.canExportFinancialAnalytics ? ['export'] : []
    };
  }
  
  // Already in new format - ensure 'create' is included when 'edit' is present
  // This fixes permissions for users created before this fix
  const ensureCreateWithEdit = (permArray) => {
    if (!permArray || !Array.isArray(permArray)) return permArray;
    if (permArray.includes('edit') && !permArray.includes('create')) {
      return [...permArray, 'create'];
    }
    return permArray;
  };
  
  // Apply fix to all permission arrays that should have create with edit
  const fixedPerms = {
    ...perms,
    bookings: ensureCreateWithEdit(perms.bookings),
    leads: ensureCreateWithEdit(perms.leads),
    expenses: ensureCreateWithEdit(perms.expenses)
  };
  
  // If expenses is not set but accounts is, mirror accounts permission to expenses
  // This maintains backward compatibility since UI groups them as "Accounts - Income/Expenses"
  if (!fixedPerms.expenses || fixedPerms.expenses.length === 0) {
    if (perms.accounts && perms.accounts.length > 0) {
      fixedPerms.expenses = [...perms.accounts];
    } else {
      fixedPerms.expenses = ['view'];
    }
  }
  
  return fixedPerms;
};

const getDefaultPermissions = () => ({
  bookings: ['view'],
  leads: ['view'],
  venues: ['view'],
  inventory: ['view'],
  expenses: ['view'],
  accounts: [],
  invoices: [],
  financials: [],
  reports: [],
  bookingAnalytics: [],
  financialAnalytics: [],
  calendar: ['view']
});

const getFullPermissions = () => ({
  bookings: ['view', 'create', 'edit', 'delete', 'export'],
  leads: ['view', 'create', 'edit', 'delete', 'export'],
  venues: ['view', 'create', 'edit', 'delete'],
  inventory: ['view', 'create', 'edit', 'delete'],
  expenses: ['view', 'create', 'edit', 'delete', 'export'],
  accounts: ['view', 'create', 'edit', 'delete', 'export'],
  invoices: ['view', 'create', 'edit', 'delete', 'export'],
  financials: ['view', 'export'],
  reports: ['view', 'export'],
  bookingAnalytics: ['export'],
  financialAnalytics: ['export'],
  calendar: ['view']
});

// Receptionist only has calendar access
const getReceptionistPermissions = () => ({
  bookings: [],
  leads: [],
  venues: [],
  inventory: [],
  expenses: [],
  accounts: [],
  invoices: [],
  financials: [],
  reports: [],
  bookingAnalytics: [],
  financialAnalytics: [],
  calendar: ['view']
});

/**
 * Hook to check user permissions for various actions
 * Admin and Master users have full access by default
 * Receptionist only has calendar access
 * Staff, Manager, and Stakeholder permissions are controlled by their admin
 * 
 * @returns {object} - Permission checking functions and loading state
 */
export const usePermissions = () => {
  const { currentUser } = useAuth();
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!currentUser) {
        setPermissions(null);
        setLoading(false);
        return;
      }
      
      // Admin and Master users have full permissions
      if (currentUser.role === 'admin' || currentUser.isMaster || currentUser.role === 'master') {
        setPermissions(getFullPermissions());
        setLoading(false);
        return;
      }
      
      // Receptionist only has calendar access
      if (currentUser.role === 'receptionist') {
        setPermissions(getReceptionistPermissions());
        setLoading(false);
        return;
      }
      
      // For staff/manager/stakeholder, use their stored permissions
      // First check if permissions are already in the currentUser object
      if (currentUser.permissions) {
        setPermissions(normalizePermissions(currentUser.permissions));
        setLoading(false);
        return;
      }
      
      // If not in currentUser, fetch from backend
      try {
        const token = localStorage.getItem('token');
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        
        const response = await fetch(`${backendUrl}/api/users/me/permissions`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setPermissions(normalizePermissions(data.permissions));
        } else {
          // Default permissions if fetch fails
          setPermissions(getDefaultPermissions());
        }
      } catch (error) {
        console.error('Failed to fetch permissions:', error);
        setPermissions(getDefaultPermissions());
      }
      
      setLoading(false);
    };

    fetchPermissions();
  }, [currentUser]);

  // Check if user is admin/master
  const isAdmin = useCallback(() => {
    return currentUser?.role === 'admin' || currentUser?.isMaster || currentUser?.role === 'master';
  }, [currentUser]);

  // Check if user can perform action on entity
  const can = useCallback((action, entity) => {
    if (!permissions) return false;
    
    // Admin and Master always have full access
    if (currentUser?.role === 'admin' || currentUser?.isMaster || currentUser?.role === 'master') {
      return true;
    }
    
    const entityPermissions = permissions[entity];
    if (!entityPermissions) return false;
    
    return entityPermissions.includes(action);
  }, [permissions, currentUser]);

  // Convenience methods
  const canViewBookings = useCallback(() => can('view', 'bookings'), [can]);
  const canCreateBookings = useCallback(() => can('create', 'bookings'), [can]);
  const canEditBookings = useCallback(() => can('edit', 'bookings'), [can]);
  const canDeleteBookings = useCallback(() => can('delete', 'bookings'), [can]);
  const canExportBookings = useCallback(() => can('export', 'bookings'), [can]);

  const canViewLeads = useCallback(() => can('view', 'leads'), [can]);
  const canCreateLeads = useCallback(() => can('create', 'leads'), [can]);
  const canEditLeads = useCallback(() => can('edit', 'leads'), [can]);
  const canDeleteLeads = useCallback(() => can('delete', 'leads'), [can]);
  const canExportLeads = useCallback(() => can('export', 'leads'), [can]);

  const canViewVenues = useCallback(() => can('view', 'venues'), [can]);
  const canCreateVenues = useCallback(() => can('create', 'venues'), [can]);
  const canEditVenues = useCallback(() => can('edit', 'venues'), [can]);
  const canDeleteVenues = useCallback(() => can('delete', 'venues'), [can]);

  const canViewInventory = useCallback(() => can('view', 'inventory'), [can]);
  const canCreateInventory = useCallback(() => can('create', 'inventory'), [can]);
  const canEditInventory = useCallback(() => can('edit', 'inventory'), [can]);
  const canDeleteInventory = useCallback(() => can('delete', 'inventory'), [can]);

  // Expenses
  const canViewExpenses = useCallback(() => can('view', 'expenses'), [can]);
  const canCreateExpenses = useCallback(() => can('create', 'expenses'), [can]);
  const canEditExpenses = useCallback(() => can('edit', 'expenses'), [can]);
  const canDeleteExpenses = useCallback(() => can('delete', 'expenses'), [can]);
  const canExportExpenses = useCallback(() => can('export', 'expenses'), [can]);

  const canViewReports = useCallback(() => can('view', 'reports'), [can]);
  const canExportReports = useCallback(() => can('export', 'reports'), [can]);

  // Analytics
  const canExportBookingAnalytics = useCallback(() => can('export', 'bookingAnalytics'), [can]);
  const canExportFinancialAnalytics = useCallback(() => can('export', 'financialAnalytics'), [can]);

  // Accounts (Income + Account Heads)
  const canViewAccounts = useCallback(() => can('view', 'accounts'), [can]);
  const canCreateAccounts = useCallback(() => can('create', 'accounts'), [can]);
  const canEditAccounts = useCallback(() => can('edit', 'accounts'), [can]);
  const canDeleteAccounts = useCallback(() => can('delete', 'accounts'), [can]);
  const canExportAccounts = useCallback(() => can('export', 'accounts'), [can]);

  // Invoices
  const canViewInvoices = useCallback(() => can('view', 'invoices'), [can]);
  const canCreateInvoices = useCallback(() => can('create', 'invoices'), [can]);
  const canEditInvoices = useCallback(() => can('edit', 'invoices'), [can]);
  const canDeleteInvoices = useCallback(() => can('delete', 'invoices'), [can]);
  const canExportInvoices = useCallback(() => can('export', 'invoices'), [can]);

  return {
    permissions,
    loading,
    can,
    isAdmin,
    // Bookings
    canViewBookings,
    canCreateBookings,
    canEditBookings,
    canDeleteBookings,
    canExportBookings,
    // Leads
    canViewLeads,
    canCreateLeads,
    canEditLeads,
    canDeleteLeads,
    canExportLeads,
    // Venues
    canViewVenues,
    canCreateVenues,
    canEditVenues,
    canDeleteVenues,
    // Inventory
    canViewInventory,
    canCreateInventory,
    canEditInventory,
    canDeleteInventory,
    // Expenses
    canViewExpenses,
    canCreateExpenses,
    canEditExpenses,
    canDeleteExpenses,
    canExportExpenses,
    // Accounts
    canViewAccounts,
    canCreateAccounts,
    canEditAccounts,
    canDeleteAccounts,
    canExportAccounts,
    // Invoices
    canViewInvoices,
    canCreateInvoices,
    canEditInvoices,
    canDeleteInvoices,
    canExportInvoices,
    // Reports
    canViewReports,
    canExportReports,
    // Analytics
    canExportBookingAnalytics,
    canExportFinancialAnalytics
  };
};

export default usePermissions;
